import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import TrialezeLogo from "@/components/shared/TrialezeLogo";
import { useAuth } from "@/contexts/AuthContext";
import { 
  LayoutDashboard, Briefcase, FileText, Users, Scale, Gavel,
  Search, Shield, BarChart3, Calculator, Camera, Settings,
  Globe, LogOut, ChevronRight
} from "lucide-react";

const PlatformLayout = ({ children, activeTab }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  const sidebarSections = [
    {
      title: "Main",
      items: [
        { id: "dashboard", label: "Command Center", icon: LayoutDashboard, path: "/dashboard" },
        { id: "cases", label: "Cases", icon: Briefcase, path: "/platform/cases" },
        { id: "scanner", label: "Doc Scanner", icon: Camera, path: "/scanner" },
      ]
    },
    {
      title: "Tools",
      items: [
        { id: "wizard", label: "Litix Wizard", icon: Gavel, path: "/tools/litix-wizard" },
        { id: "investigator", label: "Investigator", icon: Search, path: "/tools/investigator" },
        { id: "request-center", label: "Request Center", icon: FileText, path: "/tools/request-center" },
        { id: "carrier", label: "Carrier Scorecard", icon: Shield, path: "/tools/carrier-scorecard" },
        { id: "medical", label: "Medical Intel", icon: Users, path: "/tools/medical-intel" },
      ]
    },
    {
      title: "Practice Suite",
      items: [
        { id: "opening", label: "Opening Statement", icon: Gavel, path: "/tools/opening" },
        { id: "closing", label: "Closing Argument", icon: Scale, path: "/tools/closing" },
        { id: "demand", label: "Demand Letter", icon: FileText, path: "/tools/demand-letter" },
        { id: "deposition", label: "Depositions", icon: Users, path: "/tools/deposition" },
        { id: "settlement", label: "Settlement", icon: Scale, path: "/tools/settlement-negotiation" },
      ]
    },
    {
      title: "Analytics",
      items: [
        { id: "judges", label: "Judge Analytics", icon: BarChart3, path: "/tools/judges" },
        { id: "precedent", label: "Precedent Finder", icon: Search, path: "/tools/precedent-finder" },
        { id: "trends", label: "Trend Analyzer", icon: BarChart3, path: "/tools/trend-analyzer" },
        { id: "deadlines", label: "Deadlines", icon: Calculator, path: "/tools/deadlines" },
      ]
    },
  ];

  const currentPath = location.pathname;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="w-60 flex flex-col border-r border-slate-200 bg-white shrink-0">
        <Link to="/dashboard" className="flex items-center gap-2.5 px-4 py-4 border-b border-slate-100">
          <TrialezeLogo size="small" />
          <div>
            <div className="font-bold text-sm" style={{ color: '#0EA5E9' }}>Trialeze</div>
            <div className="text-[10px] text-slate-400">Legal Operating System</div>
          </div>
        </Link>

        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-4">
          {sidebarSections.map((section) => (
            <div key={section.title}>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-2 mb-1">{section.title}</p>
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const isActive = currentPath === item.path || activeTab === item.id;
                  return (
                    <Link
                      key={item.id}
                      to={item.path}
                      className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-all ${
                        isActive
                          ? "bg-blue-600 text-white font-medium"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                      }`}
                      data-testid={`sidebar-${item.id}`}
                    >
                      <item.icon className={`h-4 w-4 ${isActive ? "text-blue-100" : "text-slate-400"}`} />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-100 space-y-1">
          <Link to="/" className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50">
            <Globe className="h-4 w-4 text-slate-400" />
            <span>Website</span>
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors"
            data-testid="sidebar-logout"
          >
            <LogOut className="h-4 w-4" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default PlatformLayout;
